// 📦 Updated ScanTicket.js
import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import axios from "axios";
import "./ScanTicket.css";
import { Navigate } from "react-router-dom";

const ScanTicket = () => {
    const [scannedTickets, setScannedTickets] = useState([]);
    const [error, setError] = useState("");
    const [cameraActive, setCameraActive] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [recentlyScannedCode, setRecentlyScannedCode] = useState("");
    const [waitingForConfirm, setWaitingForConfirm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    const qrRef = useRef(null);
    const successSound = useRef(null);
    const errorSound = useRef(null);
    const tableRef = useRef(null);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const fetchScannedTickets = async () => {
        try {
            const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/tickets/scanned`);
            setScannedTickets(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        } catch (err) {
            console.error("Failed to load scanned tickets:", err);
        }
    };

    useEffect(() => {
        fetchScannedTickets();
        const interval = setInterval(fetchScannedTickets, 10000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (waitingForConfirm && scannedTickets.some(t => t.qrCode === recentlyScannedCode)) {
            setWaitingForConfirm(false);
            setRecentlyScannedCode("");
            setError("✅ Ticket scanned successfully!");
            successSound.current?.play();
            window.navigator.vibrate?.(100);
        }
    }, [scannedTickets, waitingForConfirm, recentlyScannedCode]);


    useEffect(() => {
        if (!cameraActive) return;

        const html5Qr = new Html5Qrcode("reader", { verbose: false });
        qrRef.current = html5Qr;

        const startScanner = async () => {
            try {
                const devices = await Html5Qrcode.getCameras();
                const backCamera = devices.find(d => d.label.toLowerCase().includes("back")) || devices[0];
                if (!backCamera) throw new Error("No camera found");

                await html5Qr.start(
                    { deviceId: backCamera.id },
                    { fps: 10, qrbox: 250 },
                    async (data) => {
                        if (scanning) return;
                        setScanning(true);

                        const cleanedID = data.replace("#", "").toUpperCase();

                        try {
                            await axios.post(`${process.env.REACT_APP_API_URL}/api/tickets/scan`, {
                                ticketID: cleanedID
                            });

                            setRecentlyScannedCode(cleanedID);
                            setWaitingForConfirm(true);
                            setTimeout(() => {
                                tableRef.current?.scrollIntoView({ behavior: "smooth" });
                            }, 300);


                            window.navigator.vibrate?.(100);
                        } catch (err) {
                            const msg = err.response?.data?.error || "❌ Ticket validation failed.";
                            setError(msg);
                            errorSound.current?.play();
                            window.navigator.vibrate?.(300);
                        } finally {
                            await html5Qr.stop();
                            await html5Qr.clear();
                            qrRef.current = null;
                            setCameraActive(false);
                            setScanning(false);
                            setTimeout(() => setError(""), 3000);
                        }
                    },
                    (scanError) => console.warn("Scan error:", scanError)
                );
            } catch (err) {
                console.error("Camera error:", err);
                setError("❌ Could not open camera. Check permissions.");
                errorSound.current?.play();
            }
        };

        startScanner();

        return () => {
            if (html5Qr.isScanning) {
                html5Qr.stop().then(() => html5Qr.clear()).catch(console.error);
            }
        };
    }, [cameraActive]);

    const handleActivateCamera = () => {
        setError("");
        setCameraActive(true);
    };

    const handleDownloadCSV = () => {
        const headers = ["Ticket ID", "Name", "Phone", "Class", "Status", "Time"];
        const rows = scannedTickets.map(t => [t.id, t.name, t.phone, t.class, t.status, t.time]);
        const csvContent = [headers, ...rows].map(r => r.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "scanned_attendees.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const isUsher = localStorage.getItem("isUsher") === "true";
    if (!isUsher) {
        return <Navigate to="/usher/login" />;
    }
    //<button className="download-button" onClick={handleDownloadCSV}>Download as CSV</button>put this in the last line in return

    return (
        <div className="scan-ticket-container">
            <audio ref={successSound} src="/sounds/success-beep.mp3" preload="auto" />
            <audio ref={errorSound} src="/sounds/error-beep.mp3" preload="auto" />
            {waitingForConfirm ? (
                <p className="success">⏳ Waiting for confirmation...</p>
            ) : (
                error && <p className={error.startsWith("✅") ? "success" : "error"}>{error}</p>
            )}
            {cameraActive && (
                <div className="fullscreen-scanner-wrapper">
                    <button className="close-button" onClick={() => setCameraActive(false)}>✕</button>
                    <div id="reader" className="fullscreen-scanner"></div>
                </div>
            )}

            <div className="scanned-table-section" ref={tableRef}>
                <h3>Scanned Attendees</h3>
                <table className="scanned-table">
                    <thead>
                    <tr>
                        <th>Ticket ID</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Class</th>
                        <th>Status</th>
                        <th>Time</th>
                    </tr>
                    </thead>
                    <tbody>
                    {[...scannedTickets]
                        .sort((a, b) => new Date(b.time) - new Date(a.time))
                        .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                        .map((t, index) => (

                            <tr key={index}>
                                <td>{t.id}</td>
                                <td>{t.name}</td>
                                <td>{t.phone}</td>
                                <td>{t.class}</td>
                                <td>{t.status}</td>
                                <td>{t.time}</td>
                            </tr>
                        ))}

                    </tbody>
                </table>
                <div style={{ display: "flex", justifyContent: "center", marginTop: "20px", gap: "10px" }}>
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="paddinton-button"
                    >
                        Prev
                    </button>
                    <span style={{ alignSelf: "center", fontSize: "18px", fontWeight: "bold" }}>
        Page {currentPage} of {Math.ceil(scannedTickets.length / rowsPerPage)}
    </span>
                    <button
                        onClick={() =>
                            setCurrentPage(prev => Math.min(prev + 1, Math.ceil(scannedTickets.length / rowsPerPage)))
                        }
                        disabled={currentPage >= Math.ceil(scannedTickets.length / rowsPerPage)}
                        className="paddinton-button"
                    >
                        Next
                    </button>
                </div>
            </div>
            <div className="buttons-section">
            {!cameraActive && (
                <button className="activate-button" onClick={handleActivateCamera}>
                    Activate Camera
                </button>
            )}
            </div>

        </div>
    );
};

export default ScanTicket;
